import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUpdateOrganization } from '@/graphql/mutations/organizations';
import { checkOrgSlugAvailability } from '../../services/org.service';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { Card, CardContent } from '@/components/ui-kit/card';
import type { Organization } from '../../types/org.types';
import { AlertCircle, Check, Loader2 } from 'lucide-react';

interface GeneralSettingsFormProps {
  org: Organization;
}

export const GeneralSettingsForm = ({ org }: GeneralSettingsFormProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const updateOrg = useUpdateOrganization();

  const [name, setName] = useState(org.Name);
  const [slug, setSlug] = useState(org.Slug);
  const [isCheckingSlug, setIsCheckingSlug] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
  const slugCheckTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setName(org.Name);
    setSlug(org.Slug);
    setSlugAvailable(null);
  }, [org]);

  const hasChanges = name !== org.Name || slug !== org.Slug;

  const formatSlug = (value: string) => {
    return value
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const isSlugFormatValid = (value: string) => {
    return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value);
  };

  const checkSlug = useCallback(
    async (slugValue: string) => {
      if (!slugValue || slugValue.length < 2 || slugValue === org.Slug) {
        setSlugAvailable(null);
        return;
      }
      if (!isSlugFormatValid(slugValue)) {
        setSlugAvailable(null);
        return;
      }
      setIsCheckingSlug(true);
      try {
        const available = await checkOrgSlugAvailability(slugValue, org.ItemId);
        setSlugAvailable(available);
      } catch {
        setSlugAvailable(null);
      } finally {
        setIsCheckingSlug(false);
      }
    },
    [org.ItemId, org.Slug]
  );

  const debouncedSlugCheck = useCallback(
    (slugValue: string) => {
      if (slugCheckTimeout.current) clearTimeout(slugCheckTimeout.current);
      slugCheckTimeout.current = setTimeout(() => checkSlug(slugValue), 300);
    },
    [checkSlug]
  );

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setSlug(value);
    debouncedSlugCheck(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const formattedSlug = formatSlug(slug);
    const trimmedName = name.trim();

    await updateOrg.mutateAsync({
      itemId: org.ItemId,
      Name: trimmedName,
      Slug: formattedSlug,
    });

    // Sync org name to Blocks IAM if it changed
    if (trimmedName !== org.Name) {
      // NOTE: updateOrganizationIamName has been removed as per API migration
      // IAM organization name update should be handled via the new API if needed
    }

    if (formattedSlug !== org.Slug) {
      navigate(`/org/${org.BlocksOrgId}/settings`, { replace: true });
    }
  };

  const slugFormatError = slug.length > 0 && !isSlugFormatValid(slug);
  const isSubmitDisabled =
    updateOrg.isPending || !hasChanges || slugAvailable === false || slugFormatError;

  return (
    <Card>
      <CardContent className="pt-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="name">{t('ORGSETTINGS_GENERALSETTINGS_NAMELABEL')}</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Acme Inc."
              required
              minLength={2}
            />
            <p className="text-xs text-globe-grey">
              {t('ORGSETTINGS_GENERALSETTINGS_NAMEDESCRIPTION')}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="slug">{t('ORGSETTINGS_GENERALSETTINGS_SLUGLABEL')}</Label>
            <div className="flex items-center gap-0">
              <span className="inline-flex items-center px-3 h-9 rounded-l-md border border-r-0 border-input bg-muted text-sm text-globe-grey">
                /org/
              </span>
              <div className="relative flex-1">
                <Input
                  id="slug"
                  value={slug}
                  onChange={handleSlugChange}
                  placeholder="acme-inc"
                  required
                  pattern="[a-z0-9-]+"
                  className={`rounded-l-none ${slugAvailable === false || slugFormatError
                      ? 'border-destructive pr-10'
                      : slugAvailable === true
                        ? 'border-lime-green pr-10'
                        : 'pr-10'
                    }`}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {isCheckingSlug ? (
                    <Loader2 className="size-4 animate-spin text-globe-grey" />
                  ) : slugAvailable === true ? (
                    <Check className="size-4 text-lime-green" />
                  ) : slugAvailable === false ? (
                    <AlertCircle className="size-4 text-destructive" />
                  ) : null}
                </div>
              </div>
            </div>
            {slugFormatError && (
              <p className="text-sm text-destructive">
                {t('ORGSETTINGS_GENERALSETTINGS_SLUGFORMATERROR')}
              </p>
            )}
            {slugAvailable === false && (
              <p className="text-sm text-destructive">
                {t('ORGSETTINGS_GENERALSETTINGS_SLUGTAKEN')}
              </p>
            )}
            <p className="text-xs text-globe-grey">
              {t('ORGSETTINGS_GENERALSETTINGS_SLUGDESCRIPTION')}
            </p>
          </div>

          {/* Error/Success Messages */}
          {updateOrg.error && (
            <div className="p-3 rounded-md bg-crimson/10 text-crimson text-sm">
              {updateOrg.error.message}
            </div>
          )}

          {updateOrg.isSuccess && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-[hsl(var(--lime-green))]/10 text-poly-green text-sm">
              <Check className="h-4 w-4" />
              {t('ORGSETTINGS_GENERALSETTINGS_SUCCESSMESSAGE')}
            </div>
          )}

          <div className="flex items-center gap-3 pt-2">
            <Button type="submit" disabled={isSubmitDisabled}>
              {updateOrg.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('ORGSETTINGS_GENERALSETTINGS_SAVEBUTTON')}
            </Button>
            {hasChanges && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setSlug(org.Slug);
                  setName(org.Name);
                  setSlugAvailable(null);
                }}
              >
                {t('ORGSETTINGS_GENERALSETTINGS_RESETBUTTON')}
              </Button>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
